import React from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import { InlineLoadingState } from '../../LoadingSpinner';
import { ENTRY_TYPE_LABEL, type EmployeeTimeEntryType } from '../employeeTimeEntryConstants';
import { formatHoursWithUnit } from '../payroll/payrollExportShared';
import { formatPeriodMonthTitle } from '../../../utils/employeeRoutes';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';
import { isFilterMultiSelectNone } from '../../filterMultiSelectUtils';
import { EmployeeDataExportModal } from './EmployeeDataExportModal';
import { EmployeeOverviewPeriodToolbar } from './EmployeeOverviewPeriodToolbar';
import type { PeriodGranularity } from './useEmployeePeriodQuery';

const HOUR_COLUMNS = [
  'work',
  'overtime_comp',
  'vacation',
  'sickness',
  'sickness_benefit',
  'unpaid_absence',
  'public_holiday',
] as const satisfies ReadonlyArray<EmployeeTimeEntryType | 'sickness_benefit' | 'unpaid_absence'>;

type OverviewHourColumn = (typeof HOUR_COLUMNS)[number];

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function avatarClass(colorIdx: number | undefined | null) {
  const idx = Number.isFinite(Number(colorIdx)) ? Number(colorIdx) : 0;
  return AVATAR_COLORS[((idx % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length];
}

function formatUtilizationPct(pct: number | null | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return '-';
  return `${Math.round(pct)}%`;
}

function utilizationTextClass(pct: number | null | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return 'text-app-text-subtle';
  if (pct > 110) return 'text-red-600 font-semibold';
  if (pct > 95) return 'text-amber-600 font-medium';
  if (pct > 50) return 'text-emerald-700 font-medium';
  return 'text-app-text-muted';
}

function utilizationNumeratorHours(emp: OverviewEmployee): number {
  const h = emp.hours_by_type;
  return roundHours(
    emp.actual_work_hours + h.vacation + h.sickness + h.sickness_benefit + h.public_holiday,
  );
}

function sicknessDisplayHours(h: OverviewEmployee['hours_by_type']): number {
  return roundHours(h.sickness + h.sickness_benefit);
}

function aggregateUtilizationPct(numeratorSum: number, targetSum: number): number | null {
  if (!Number.isFinite(targetSum) || targetSum <= 0) return null;
  return Math.round((numeratorSum / targetSum) * 100);
}

function departmentLabel(emp: Pick<OverviewEmployee, 'department' | 'position'>): string {
  return [emp.department, emp.position].filter(Boolean).join(' · ') || '-';
}

type OverviewEmployee = {
  id: string;
  name: string;
  color_idx: number;
  department: string;
  position: string;
  compensation_type?: 'hourly' | 'salary';
  hours_by_type: Record<OverviewHourColumn, number>;
  target_work_hours: number;
  actual_work_hours: number;
  utilization_pct: number | null;
  total_hours: number;
};

type OverviewKpis = {
  totalHours: number;
  workHours: number;
  overtimeCompHours: number;
  sicknessHours: number;
  vacationHours: number;
  publicHolidayHours: number;
  pendingApprovals: number;
  employeeCount: number;
};

type PendingClosePeriod = {
  id: string;
  year: number;
  month: number;
  year_month: string;
  label: string;
};

/** Collapsed stack shows this many period cards; older ones first. */
const MAX_COLLAPSED_PENDING_CLOSE = 1;

type EmployeeOverviewDashboardTabProps = {
  from: string;
  to: string;
  granularity: PeriodGranularity;
  onGranularityChange: (g: 'month' | 'week') => void;
  onShift: (direction: -1 | 1) => void;
  onApplyCustomPeriod: (range: { from: string; to: string }) => void;
  onRowClick: (employeeId: string) => void;
  onOpenRequestsTab?: () => void;
};

function formatHours(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '-';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

function roundHours(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Einheitliche Breite für Stunden-/Prozent-Spalten (verhindert Aufblähen durch lange Header). */
const HOUR_COL_WIDTH = '4.75rem';

const TABLE_COLUMNS = [
  ['name', 'Mitarbeiter', 'left'],
  ['department', 'Abteilung', 'left'],
  ['target_work', 'Soll-Arbeit', 'right'],
  ['actual_work', 'Ist-Arbeit', 'right'],
  ['utilization', 'Auslastung', 'right'],
  ['overtime_comp', 'Freizeitausgl.', 'right'],
  ['vacation', 'Urlaub', 'right'],
  ['sickness', 'Krank', 'right'],
  ['unpaid_absence', 'Fehlzeit', 'right'],
  ['public_holiday', ENTRY_TYPE_LABEL.public_holiday, 'right'],
  ['total', 'Summe', 'right'],
] as const;

type OverviewSortDir = 'asc' | 'desc';

function OverviewSortableHeader({
  label,
  active,
  dir,
  align,
  title,
  onSort,
}: {
  label: string;
  active: boolean;
  dir: OverviewSortDir;
  align: 'left' | 'right';
  title?: string;
  onSort: () => void;
}) {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-2 py-2.5 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={onSort}
        title={title}
        className={`group/sort inline-flex max-w-full items-center gap-1 text-xs font-medium whitespace-nowrap transition-colors ${
          active ? 'text-app-text' : 'text-app-text-muted hover:text-app-text'
        } ${align === 'right' ? 'ml-auto justify-end' : ''}`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

function computeKpis(rows: OverviewEmployee[]): OverviewKpis {
  let totalHours = 0;
  let workHours = 0;
  let overtimeCompHours = 0;
  let sicknessHours = 0;
  let vacationHours = 0;
  let publicHolidayHours = 0;
  for (const row of rows) {
    totalHours += row.total_hours;
    workHours += row.hours_by_type.work + row.hours_by_type.overtime_comp;
    overtimeCompHours += row.hours_by_type.overtime_comp;
    sicknessHours += row.hours_by_type.sickness + row.hours_by_type.sickness_benefit;
    vacationHours += row.hours_by_type.vacation;
    publicHolidayHours += row.hours_by_type.public_holiday;
  }
  totalHours = roundHours(totalHours);
  workHours = roundHours(workHours);
  overtimeCompHours = roundHours(overtimeCompHours);
  sicknessHours = roundHours(sicknessHours);
  return {
    totalHours,
    workHours,
    overtimeCompHours,
    sicknessHours,
    vacationHours: roundHours(vacationHours),
    publicHolidayHours: roundHours(publicHolidayHours),
    pendingApprovals: 0,
    employeeCount: rows.length,
  };
}

function KpiTile({
  label,
  value,
  unit,
  onClick,
}: {
  label: string;
  value: string;
  unit?: string;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <p className="text-xs font-medium text-app-text-muted truncate">{label}</p>
      <p className="mt-1.5 flex items-baseline gap-1">
        <span className="text-xl font-semibold text-app-text tabular-nums">{value}</span>
        {unit ? <span className="text-xs font-medium text-app-text-muted">{unit}</span> : null}
      </p>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="bg-app-surface border border-app-border rounded-lg px-4 py-3.5 min-w-0 text-left hover:border-pipedrive-blue/40 hover:bg-blue-50/30 transition-colors"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="bg-app-surface border border-app-border rounded-lg px-4 py-3.5 min-w-0">
      {inner}
    </div>
  );
}

export function EmployeeOverviewDashboardTab({
  from,
  to,
  granularity,
  onGranularityChange,
  onShift,
  onApplyCustomPeriod,
  onRowClick,
  onOpenRequestsTab,
}: EmployeeOverviewDashboardTabProps) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [employees, setEmployees] = React.useState<OverviewEmployee[]>([]);
  const [pendingApprovals, setPendingApprovals] = React.useState(0);
  const [pendingClosePeriods, setPendingClosePeriods] = React.useState<PendingClosePeriod[]>([]);
  const [pendingCloseExpanded, setPendingCloseExpanded] = React.useState(false);
  const [isPayrollApprover, setIsPayrollApprover] = React.useState(false);
  const [closingPeriod, setClosingPeriod] = React.useState<string | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<string[]>([]);
  const [sort, setSort] = React.useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'total', dir: 'desc' });
  const [tableSearch, setTableSearch] = React.useState('');
  const [exportOpen, setExportOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const params = new URLSearchParams({ from, to });
        const [summaryRes, meRes] = await Promise.all([
          fetch(`/api/employees/overview-summary?${params}`),
          fetch('/api/me', { credentials: 'include' }),
        ]);
        const json = (await summaryRes.json()) as {
          employees?: OverviewEmployee[];
          kpis?: { pendingApprovals?: number };
          pendingClosePeriods?: PendingClosePeriod[];
          error?: string;
        };
        if (!summaryRes.ok) throw new Error(json.error || 'Übersicht konnte nicht geladen werden.');
        if (meRes.ok) {
          const me = (await meRes.json()) as { is_payroll_approver?: boolean };
          if (!cancelled) setIsPayrollApprover(Boolean(me.is_payroll_approver));
        }
        if (cancelled) return;
        const rows = (Array.isArray(json.employees) ? json.employees : []).map((raw) => ({
          ...raw,
          hours_by_type: {
            work: raw.hours_by_type?.work ?? 0,
            overtime_comp: raw.hours_by_type?.overtime_comp ?? 0,
            vacation: raw.hours_by_type?.vacation ?? 0,
            sickness: raw.hours_by_type?.sickness ?? 0,
            sickness_benefit: raw.hours_by_type?.sickness_benefit ?? 0,
            unpaid_absence: raw.hours_by_type?.unpaid_absence ?? 0,
            public_holiday: raw.hours_by_type?.public_holiday ?? 0,
          },
        }));
        setEmployees(rows);
        setSelectedEmployeeIds([]);
        setPendingApprovals(json.kpis?.pendingApprovals ?? 0);
        setPendingClosePeriods(Array.isArray(json.pendingClosePeriods) ? json.pendingClosePeriods : []);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Übersicht konnte nicht geladen werden.');
          setEmployees([]);
          setSelectedEmployeeIds([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const filterEmployees = React.useMemo(
    () =>
      employees.map((e) => ({
        id: e.id,
        name: e.name,
        color_idx: e.color_idx ?? 0,
        compensation_type: e.compensation_type === 'salary' ? 'salary' : 'hourly',
      })),
    [employees],
  );

  const filtered = React.useMemo(() => {
    if (isFilterMultiSelectNone(selectedEmployeeIds)) return [];
    if (selectedEmployeeIds.length === 0) return employees;
    const allowed = new Set(selectedEmployeeIds);
    return employees.filter((e) => allowed.has(e.id));
  }, [employees, selectedEmployeeIds]);

  const exportModalSelectedEmployeeIds = React.useMemo(
    () => filtered.map((e) => e.id),
    [filtered],
  );

  const displayKpis = React.useMemo(
    () => ({ ...computeKpis(filtered), pendingApprovals }),
    [filtered, pendingApprovals],
  );

  const handleClosePeriod = async (yearMonth: string) => {
    setClosingPeriod(yearMonth);
    setError(null);
    try {
      const res = await fetch(`/api/employees/payroll-periods/${encodeURIComponent(yearMonth)}/close`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Abrechnungszeitraum konnte nicht abgeschlossen werden.');
      setPendingClosePeriods((prev) => prev.filter((p) => p.year_month !== yearMonth));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Abrechnungszeitraum konnte nicht abgeschlossen werden.');
    } finally {
      setClosingPeriod(null);
    }
  };

  React.useEffect(() => {
    if (pendingClosePeriods.length <= MAX_COLLAPSED_PENDING_CLOSE) {
      setPendingCloseExpanded(false);
    }
  }, [pendingClosePeriods.length]);

  const sortedPendingClosePeriods = React.useMemo(
    () =>
      [...pendingClosePeriods].sort((a, b) =>
        a.year !== b.year ? a.year - b.year : a.month - b.month,
      ),
    [pendingClosePeriods],
  );

  const visiblePendingClosePeriods = pendingCloseExpanded
    ? sortedPendingClosePeriods
    : sortedPendingClosePeriods.slice(0, MAX_COLLAPSED_PENDING_CLOSE);
  const hiddenPendingCloseCount = Math.max(
    0,
    sortedPendingClosePeriods.length - visiblePendingClosePeriods.length,
  );
  const showPendingCloseStack =
    !pendingCloseExpanded && sortedPendingClosePeriods.length > MAX_COLLAPSED_PENDING_CLOSE;

  const columnTotals = React.useMemo(() => {
    const byType = Object.fromEntries(HOUR_COLUMNS.map((t) => [t, 0])) as Record<OverviewHourColumn, number>;
    let targetWork = 0;
    let actualWork = 0;
    let utilizationNumerator = 0;
    let totalHours = 0;
    for (const row of filtered) {
      for (const t of HOUR_COLUMNS) {
        byType[t] += row.hours_by_type[t];
      }
      targetWork += row.target_work_hours;
      actualWork += row.actual_work_hours;
      utilizationNumerator += utilizationNumeratorHours(row);
      totalHours += row.total_hours;
    }
    for (const t of HOUR_COLUMNS) {
      byType[t] = roundHours(byType[t]);
    }
    targetWork = roundHours(targetWork);
    actualWork = roundHours(actualWork);
    utilizationNumerator = roundHours(utilizationNumerator);
    return {
      byType,
      targetWork,
      actualWork,
      utilizationPct: aggregateUtilizationPct(utilizationNumerator, targetWork),
      totalHours: roundHours(totalHours),
    };
  }, [filtered]);

  // Die Tabelle ist breiter als der Viewport — Shift+Rad ersetzt das Ziehen der Leiste.
  const setTableScrollNode = useHorizontalWheelScroll<HTMLDivElement>();

  const sorted = React.useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    const arr = filtered.filter(
      (e) =>
        !q ||
        e.name.toLowerCase().includes(q) ||
        e.department.toLowerCase().includes(q) ||
        e.position.toLowerCase().includes(q),
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sort.key) {
        case 'name':
          return a.name.localeCompare(b.name, 'de') * dir;
        case 'department':
          return departmentLabel(a).localeCompare(departmentLabel(b), 'de') * dir;
        case 'target_work':
          return (a.target_work_hours - b.target_work_hours) * dir;
        case 'actual_work':
          return (a.actual_work_hours - b.actual_work_hours) * dir;
        case 'utilization':
          return ((a.utilization_pct ?? -1) - (b.utilization_pct ?? -1)) * dir;
        case 'overtime_comp':
          return (a.hours_by_type.overtime_comp - b.hours_by_type.overtime_comp) * dir;
        case 'vacation':
          return (a.hours_by_type.vacation - b.hours_by_type.vacation) * dir;
        case 'sickness':
          return (sicknessDisplayHours(a.hours_by_type) - sicknessDisplayHours(b.hours_by_type)) * dir;
        case 'unpaid_absence':
          return (a.hours_by_type.unpaid_absence - b.hours_by_type.unpaid_absence) * dir;
        case 'public_holiday':
          return (a.hours_by_type.public_holiday - b.hours_by_type.public_holiday) * dir;
        default:
          return (a.total_hours - b.total_hours) * dir;
      }
    });
    return arr;
  }, [filtered, sort, tableSearch]);

  const toggleSort = (key: string) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <EmployeeOverviewPeriodToolbar
        from={from}
        to={to}
        granularity={granularity}
        onGranularityChange={onGranularityChange}
        onShift={onShift}
        onApplyCustomPeriod={onApplyCustomPeriod}
        filterEmployees={filterEmployees}
        selectedEmployeeIds={selectedEmployeeIds}
        onSelectedEmployeeIdsChange={setSelectedEmployeeIds}
        onOpenExport={() => setExportOpen(true)}
      />

      <EmployeeDataExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        employees={filterEmployees}
        initialSelectedEmployeeIds={exportModalSelectedEmployeeIds}
      />

      {error && (
        <div className="shrink-0 mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <InlineLoadingState label="Lade Übersicht…" className="flex-1 py-8" />
      ) : (
        <>
          <div className="shrink-0 px-5 pt-4 pb-3 bg-app-bg border-b border-app-border/80 space-y-3">
            {sortedPendingClosePeriods.length > 0 ? (
              <div className="space-y-2">
                <div className={showPendingCloseStack ? 'relative pb-2' : undefined}>
                  {showPendingCloseStack ? (
                    <>
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-3 top-2 bottom-0 rounded-lg border border-amber-200/70 bg-amber-50/80 dark:border-app-border dark:bg-app-surface-muted"
                      />
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-1.5 top-1 bottom-0 rounded-lg border border-amber-200/85 bg-amber-50/90 dark:border-app-border dark:bg-app-surface-muted"
                      />
                    </>
                  ) : null}
                  <div className={`relative space-y-2 ${showPendingCloseStack ? 'z-[1]' : ''}`}>
                    {visiblePendingClosePeriods.map((period) => (
                      <div
                        key={period.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-amber-950">
                            Abrechnungszeitraum {period.label} abschließen
                          </p>
                          <p className="text-xs text-amber-800 mt-0.5">
                            Nach dem Abschluss sind Änderungen nur noch über Freigabe möglich.
                          </p>
                        </div>
                        {isPayrollApprover ? (
                          <button
                            type="button"
                            disabled={closingPeriod === period.year_month}
                            onClick={() => { void handleClosePeriod(period.year_month); }}
                            className="inline-flex h-9 shrink-0 items-center rounded-md bg-pipedrive-blue px-3.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            {closingPeriod === period.year_month ? 'Wird abgeschlossen…' : 'Abschließen'}
                          </button>
                        ) : (
                          <span className="text-xs text-amber-800 shrink-0">
                            Nur Lohnbuchhaltungs-Freigeber können abschließen.
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {sortedPendingClosePeriods.length > MAX_COLLAPSED_PENDING_CLOSE ? (
                  <button
                    type="button"
                    onClick={() => setPendingCloseExpanded((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:text-amber-950"
                  >
                    {pendingCloseExpanded ? (
                      <>
                        <ChevronUp className="h-3.5 w-3.5" />
                        Weniger anzeigen
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3.5 w-3.5" />
                        {hiddenPendingCloseCount} weitere Abrechnungszeiträume anzeigen
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              <KpiTile label="Gesamtstunden" value={formatHours(displayKpis.totalHours)} unit="h" />
              <KpiTile label="Arbeitsstunden" value={formatHours(displayKpis.workHours)} unit="h" />
              <KpiTile
                label={ENTRY_TYPE_LABEL.overtime_comp}
                value={formatHours(displayKpis.overtimeCompHours)}
                unit="h"
              />
              <KpiTile label="Krankheitsstunden" value={formatHours(displayKpis.sicknessHours)} unit="h" />
              <KpiTile label="Urlaubsstunden" value={formatHours(displayKpis.vacationHours)} unit="h" />
              <KpiTile
                label={ENTRY_TYPE_LABEL.public_holiday}
                value={formatHours(displayKpis.publicHolidayHours)}
                unit="h"
              />
              <KpiTile
                label="Offene Genehmigungen"
                value={String(displayKpis.pendingApprovals)}
                onClick={displayKpis.pendingApprovals > 0 && onOpenRequestsTab ? onOpenRequestsTab : undefined}
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col px-5 pb-5 pt-3 min-w-0">
            <div className="flex flex-col max-h-full min-h-0 bg-app-surface border border-app-border rounded-lg overflow-hidden min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-app-border shrink-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-app-text">Stunden je Mitarbeiter</p>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {sorted.length} von {filtered.length} · {formatPeriodMonthTitle(from)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-app-border bg-app-bg px-2.5 py-1">
                  <Search className="h-3.5 w-3.5 text-app-text-subtle shrink-0" />
                  <input
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    placeholder="Mitarbeiter durchsuchen"
                    className="w-[180px] border-0 bg-transparent text-xs text-app-text-muted outline-none placeholder:text-app-text-subtle"
                  />
                </div>
              </div>

              {sorted.length === 0 ? (
                <p className="px-4 py-8 text-sm text-app-text-subtle text-center">Keine Mitarbeiter in dieser Ansicht.</p>
              ) : (
                <div
                  ref={setTableScrollNode}
                  className="app-scrollbar min-h-0 flex-1 overflow-auto overscroll-contain"
                >
                  <table className="w-full min-w-[1180px] table-fixed text-sm">
                    <colgroup>
                      <col style={{ width: '200px' }} />
                      <col style={{ width: '160px' }} />
                      {Array.from({ length: 9 }).map((_, i) => (
                        <col key={i} style={{ width: HOUR_COL_WIDTH }} />
                      ))}
                      <col style={{ width: '2rem' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        {TABLE_COLUMNS.map(([key, label, align]) => (
                          <OverviewSortableHeader
                            key={key}
                            label={label}
                            active={sort.key === key}
                            dir={sort.dir}
                            align={align}
                            title={key === 'unpaid_absence' ? ENTRY_TYPE_LABEL.unpaid_absence : undefined}
                            onSort={() => toggleSort(key)}
                          />
                        ))}
                        <th
                          scope="col"
                          className="sticky top-0 z-10 w-8 border-b border-app-border bg-app-surface-muted px-1 py-2.5"
                        />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-app-border">
                      {sorted.map((emp) => {
                        const av = avatarClass(emp.color_idx);
                        const h = emp.hours_by_type;
                        return (
                          <tr
                            key={emp.id}
                            className="cursor-pointer hover:bg-app-hover"
                            onClick={() => onRowClick(emp.id)}
                          >
                            <td className="px-2.5 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-2.5">
                                <span
                                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${av.bg} ${av.text}`}
                                >
                                  {initialsFromName(emp.name)}
                                </span>
                                <span className="font-medium text-app-text max-w-[200px] truncate">{emp.name}</span>
                              </div>
                            </td>
                            <td className="px-2.5 py-2 text-app-text-muted max-w-[220px] truncate" title={departmentLabel(emp)}>
                              {departmentLabel(emp)}
                            </td>
                            <td className="px-2.5 py-2 text-right tabular-nums text-app-text-muted whitespace-nowrap">
                              {formatHoursWithUnit(emp.target_work_hours)}
                            </td>
                            <td className="px-2.5 py-2 text-right tabular-nums text-app-text whitespace-nowrap">
                              {formatHoursWithUnit(emp.actual_work_hours)}
                            </td>
                            <td
                              className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${utilizationTextClass(emp.utilization_pct)}`}
                              title={
                                emp.target_work_hours > 0
                                  ? `${formatHours(utilizationNumeratorHours(emp))} h (Ist + Urlaub + Feiertag + Krank + Krankengeld) von ${formatHours(emp.target_work_hours)} h Soll`
                                  : undefined
                              }
                            >
                              {formatUtilizationPct(emp.utilization_pct)}
                            </td>
                            <td
                              className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                                h.overtime_comp > 0 ? 'text-violet-700 font-medium' : 'text-app-text-subtle'
                              }`}
                              title="Reduziert die Arbeitsstunden"
                            >
                              {h.overtime_comp > 0 ? `−${formatHours(h.overtime_comp)} h` : '-'}
                            </td>
                            <td
                              className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                                h.vacation > 0 ? 'text-app-text-muted' : 'text-app-text-subtle'
                              }`}
                            >
                              {formatHoursWithUnit(h.vacation)}
                            </td>
                            <td
                              className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                                sicknessDisplayHours(h) > 0 ? 'text-red-700 font-medium' : 'text-app-text-subtle'
                              }`}
                            >
                              {formatHoursWithUnit(sicknessDisplayHours(h))}
                            </td>
                            <td
                              className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                                h.unpaid_absence > 0 ? 'text-orange-800 font-medium' : 'text-app-text-subtle'
                              }`}
                            >
                              {formatHoursWithUnit(h.unpaid_absence)}
                            </td>
                            <td
                              className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                                h.public_holiday > 0 ? 'text-emerald-700' : 'text-app-text-subtle'
                              }`}
                            >
                              {formatHoursWithUnit(h.public_holiday)}
                            </td>
                            <td className="px-2.5 py-2 text-right font-semibold tabular-nums text-app-text whitespace-nowrap">
                              {formatHoursWithUnit(emp.total_hours)}
                            </td>
                            <td className="px-1 py-2 text-app-text-subtle">
                              <ChevronRight className="h-3.5 w-3.5 mx-auto" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-app-surface-muted text-xs font-semibold border-t-2 border-app-border">
                        <td colSpan={2} className="px-2.5 py-3 text-app-text-subtle">
                          Summe ({sorted.length} Mitarbeiter)
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-app-text-muted">
                          {formatHoursWithUnit(columnTotals.targetWork)}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-app-text">
                          {formatHoursWithUnit(columnTotals.actualWork)}
                        </td>
                        <td
                          className={`px-2.5 py-3 text-right tabular-nums ${utilizationTextClass(columnTotals.utilizationPct)}`}
                        >
                          {formatUtilizationPct(columnTotals.utilizationPct)}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-violet-700">
                          {columnTotals.byType.overtime_comp > 0
                            ? `−${formatHours(columnTotals.byType.overtime_comp)} h`
                            : '-'}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-app-text">
                          {formatHoursWithUnit(columnTotals.byType.vacation)}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-red-700">
                          {formatHoursWithUnit(
                            roundHours(columnTotals.byType.sickness + columnTotals.byType.sickness_benefit),
                          )}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-orange-800">
                          {formatHoursWithUnit(columnTotals.byType.unpaid_absence)}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-emerald-700">
                          {formatHoursWithUnit(columnTotals.byType.public_holiday)}
                        </td>
                        <td className="px-2.5 py-3 text-right tabular-nums text-sm font-bold text-app-text">
                          {formatHoursWithUnit(columnTotals.totalHours)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            <p className="shrink-0 mt-2 text-xs text-app-text-subtle">
              Zeile anklicken öffnet die Zeiterfassung des Mitarbeiters. Klick auf den Zeitraum für ein freies Datum.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
